import 'Guide/entrypoint/guidepage';
import { ___p } from '@ifixit/localize';
import { createTippy } from 'Shared/utils/lazy-tippy';
import 'Shared/tippy-legacy-theme.css';
import { Modal } from 'Shared/modal';
import { Auth } from 'Shared/auth';
import { FormManager } from 'Shared/form_manager';
import ConfirmDelete from './confirm_delete';
import { initOembedContainers } from 'Shared/remote_embed';
import { AutoPreview } from 'Guide/autopreview';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import {
   clearContent,
   getSerializedText,
   getText,
   clearSessionStorage,
} from 'Shared/prosemirror/tiptap/editor_cache';
import 'Shared/Campaigns/campaign-banner-wp';
import { createRoot } from 'react-dom/client';

initOembedContainers();

onDomReady(() => {
   const SORT_DEFAULT = 'Default'; // Matches Question::SORT_DEFAULT
   const questionid = App.questionid;
   let allCommentsLoaded = false;
   let activeFilterTab = $E('.filter-option-active');
   let currentSort =
      getSortParamValue() || (activeFilterTab && activeFilterTab.dataset.filter) || SORT_DEFAULT;
   let editorid;
   let answerFormManager;
   let commentFormManager;
   let commentEditFormManager;

   const elementAbovePM = document.querySelector('.answer-input-title');

   if (elementAbovePM) {
      const showPMOptions = {
         root: null,
         rootMargin: '0px',
         threshold: 0,
      };

      const showProseMirror = async function (entries, observer) {
         const isInView = entries[0].target === elementAbovePM && entries[0].isIntersecting;

         if (isInView) {
            await getEditor();
         }
      };

      const observer = new IntersectionObserver(showProseMirror, showPMOptions);
      observer.observe(elementAbovePM);
   }

   /**
    * Get an attribute of the provided element, or it's nearest parent that has
    * a value; null otherwise.
    */
   function getParentAttribute(el, attr) {
      let val = el.get(attr);
      if (val === null) {
         el = el.getParent('[' + attr + ']');
         val = el && el.get(attr);
      }
      return val;
   }

   function showHiddenComments(moreElement) {
      const postid = moreElement.get('data-postid');

      const showComments = () => {
         // All the comments-more links get replaced in replaceAnswersAndQuestionHtml,
         // so we need to re-find the button.
         moreElement = $('comments-more-' + postid);
         moreElement.getSiblings('.js-comments-extra').removeClass('hidden');
         moreElement.hide();
      };
      if (allCommentsLoaded) {
         showComments();
      } else {
         replaceAnswersAndQuestionHtml().then(showComments);
      }
   }

   // Copied from utils.js onHashFragmentElement()
   function watchHashChange(handler) {
      function callHandlerWithElement() {
         if (window.location.hash) {
            const id = window.location.hash.slice(1);
            const element = $(id);

            if (element) {
               handler(element);
            } else if (!allCommentsLoaded && id.startsWith('comment')) {
               // Load extra comments first.
               replaceAnswersAndQuestionHtml().then(() => {
                  const element = $(id);
                  if (element) {
                     const parent = element.parentElement;
                     if (parent?.tagName === 'DETAILS') {
                        parent.open = true;
                     }
                     handler(element);
                  }
               });
            }
         }
      }

      onDomReady(callHandlerWithElement);
      window.addEventListener('hashchange', callHandlerWithElement);
   }

   // If the url hash refers to a hidden comment, show all comments
   watchHashChange(comment => {
      if (!comment.visible()) {
         let more = comment.getSiblings('.js-comments-more');

         if (more[0]) {
            showHiddenComments(more[0]);

            // Wait until the page loads so that the scroll position can be
            // calculated.
            onLoad(() => {
               comment.jumpTo({
                  offset: 0,
                  behavior: 'instant',
               });
            });
         }
      }
   });

   function updateAnswerText() {
      const value = getSerializedText(editorid);
      $('qaText').set('value', value);
   }

   when($('answer-form'), answerForm => {
      answerFormManager = new FormManager(answerForm);
      let answerTextArea = $('qaText');

      function postAsComment() {
         let firstPost = $E('.js-user-first-post');
         if (firstPost) {
            let postid = firstPost.get('data-postid');
            let userid = firstPost.get('data-userid');
            let form = firstPost.getElement('.js-comment-form');

            firstPost.getElement('.js-add-comment').hide();
            initializeCommentForm(form, answerTextArea.value.length);
            form.show();
            let textarea = form.getElement('textarea');

            textarea.set('value', getSerializedText(editorid));
            clearContent(editorid);
            textarea.focus();
         }
      }

      answerFormManager.setValidator('qaText', () => {
         let text = getText(editorid);
         text = text.replace(/^\s+/g, ''); // strip leading
         text = text.replace(/\s+$/g, ''); // strip trailing

         if (text.length === 0) {
            return _js('Please answer the question.');
         }

         if (text.length < App.minAnswerLength) {
            return answerFormManager.requiredError(
               _js(
                  'Body must be at least %1 characters, you entered %2',
                  App.minAnswerLength,
                  text.length
               )
            );
         }

         text.replace('\r\n', '\n');
         text.replace('\r', '\n');
         let parts = text.split('\n');
         if (parts.length > App.maxAnswerLines) {
            return _js(
               'This post has more than %1 lines. You should shorten it.',
               App.maxAnswerLines
            );
         }
      });

      answerFormManager.addSubmitHandler(() => {
         updateAnswerText();

         const trackAnswerSubmit = () => {
            trackAnalyticsCustomEvent({
               eventCategory: 'Add Answer',
               eventAction: 'Add Answer - Form - Submit',
            });
         };

         const handleAnswerSubmit = () => {
            if (window.iFixit_clickedSubmit) {
               return;
            }

            trackAnswerSubmit();
            window.iFixit_clickedSubmit = true;

            clearSessionStorage(editorid);

            answerFormManager.submitNow();
         };

         Auth.required({
            message: _js('Log in to answer a question.'),
            onAuthorize: function () {
               let buttons = {};

               if (answerForm.hasClass('js-user-answering-only-question')) {
                  buttons[_js('Post as comment')] = postAsComment;
                  buttons[_js('Update question')] = function () {
                     if (!window.iFixit_clickedSubmit) {
                        window.iFixit_clickedSubmit = true;
                        const value = getSerializedText(editorid);
                        $('updateText').set('value', value);

                        clearSessionStorage(editorid);

                        $('updateForm').submit();
                     }
                  };
                  buttons[_js('Answer anyway')] = handleAnswerSubmit;
                  buttons[_js('Cancel')] = function () {};

                  Modal.open({
                     type: 'message',
                     message: htmlEncode(
                        _js(
                           'You are writing an answer to your own question. %1 If you wish to add clarifying information or update your question, please do so with a comment, instead of an answer.'
                        )
                     ).replace('%1', '<br><br>'),
                     buttons: buttons,
                  });
               } else if (answerForm.hasClass('js-user-answering-again')) {
                  buttons[_js('Post as comment')] = postAsComment;
                  buttons[_js('Update answer')] = function () {
                     window.iFixit_clickedSubmit = true;
                     clearSessionStorage(editorid);
                     $('updateText').set('value', answerTextArea.get('value'));
                     $('updateForm').submit();
                  };
                  buttons[_js('Answer anyway')] = handleAnswerSubmit;
                  buttons[_js('Cancel')] = function () {};

                  Modal.open({
                     type: 'message',
                     message: htmlEncode(
                        _js(
                           '%1You have already answered this.%2 If at all possible, update your answer or add a comment instead of posting a new answer.'
                        )
                     )
                        .replace('%1', '<strong>')
                        .replace('%2', '</strong><br><br>'),
                     buttons: buttons,
                  });
               } else {
                  handleAnswerSubmit();
               }
            },
         });
      });

      let answerAutoPreview = new AutoPreview(
         'answer-form',
         'answerPreviewContainer',
         'getPreview',
         {},
         {
            class: 'qaText',
            styles: {
               'border-top': 'none',
            },
         }
      );
   });

   $('content').addEvents({
      'click:relay(.js-preview-answer)': function (ev, el) {
         ev.stop();
         updateAnswerText();
         let formManager = $('answer-form').retrieve('FormManager:formManager');

         formManager.validate(() => {
            $('answerPreviewContainer').show();
            window.scrollTo(0, $('answerPreviewContainer').getPosition().y);
         });
      },
      'click:relay(.js-toggle-selected)': function (ev, el) {
         let isSelected = el.hasClass('selected');
         $$('.js-toggle-target').removeClass('toggle-open');
         $$('.js-toggle-selected').removeClass('selected');

         let target = el.getParent().getElement('.js-toggle-target');
         if (isSelected) {
            target.removeClass('toggle-open');
            el.removeClass('selected');
         } else {
            target.addClass('toggle-open');
            el.addClass('selected');
         }
      },
      'click:relay(.js-filter-option)': function (ev, el) {
         ev.stop();
         currentSort = el.get('data-filter');
         let listElement = el.getParent();
         let toggleButton = listElement.getParent().getElement('.js-toggle-selected');

         trackAnalyticsCustomEvent({
            eventCategory: 'Forum Questions Filter',
            eventAction: `Forum Questions Filter - ${currentSort}`,
         });

         replaceAnswersAndQuestionHtml().then(() => {
            el.addClass('filter-option-active');
            listElement.removeClass('toggle-open');
            toggleButton.removeClass('selected');
            updateSortParam(currentSort);
         });
      },
      'click:relay(.js-score-history)': function (ev, el) {
         let postid = getParentAttribute(el, 'data-postid');
         Auth.required({
            message: _js('Log in to view this posts voting history.'),
            onAuthorize: function () {
               new Request.AjaxIO('getVotesOnPost', {
                  onSuccess: function (response) {
                     if (response.error) {
                        return response.error == 'no_display' ? false : false;
                     } else {
                        let element = new Element('div');
                        element.set('html', response.html);
                        Modal.open({
                           type: 'element',
                           element: element,
                        });
                     }
                  },
               }).send(postid);
            },
         });
      },
      'click:relay(.js-add-comment)': function (ev, el) {
         let parent = el.getParent('.js-post');
         el.hide();
         let form = el.getNext('.js-comment-form');
         initializeCommentForm(form);
         form.show();
         form.getElement('textarea').focus();
      },
      'click:relay(.js-button-vote)': function (ev, el) {
         let postid = getParentAttribute(el, 'data-postid');
         let voteDirection = el.get('data-voteDirection');
         let currentVoteDirection = getParentAttribute(el, 'data-currentVote');

         // Determine the vote type based on the element's context
         const isQuestion = el.closest('.js-post-question') !== null;
         const isUp = voteDirection === 'up';
         const voteType = isQuestion ? 'Good question' : 'Helpful answer';
         const voteValue = isUp ? 'YES' : 'NO';

         trackAnalyticsCustomEvent({
            eventCategory: 'View Answer',
            eventAction: `View Answer - Btn ${voteType} ${voteValue}`,
         });

         postVoteAction(el, voteDirection, currentVoteDirection, postid);
      },
      'click:relay(.js-cancel-comment)': function (ev, el) {
         ev.stop();
         el.getParent('.js-comment-form').hide();
         el.getParent('.js-add-comment-container').getElement('.js-add-comment').show();
      },
      'click:relay(.js-cancel-edit-comment)': function (ev, el) {
         ev.stop();
         let parent = el.getParent('.js-comment');
         let editContainer = parent.getElement('.js-comment-edit-container');
         let commentText = parent.getElement('.js-comment-text');
         let commentMeta = parent.getElement('.comment-meta');
         editContainer.hide();
         commentText.show();
         commentMeta.show();
      },
      'click:relay(.js-comment-edit)': function (ev, el) {
         ev.stop();

         Auth.required({
            onAuthorize: function () {
               let parent = el.getParent('.js-comment');
               let editContainer = parent.getElement('.js-comment-edit-container');
               let commentText = parent.getElement('.js-comment-text');
               let commentMeta = parent.getElement('.comment-meta');
               const trackCommentEdit = () => {
                  trackAnalyticsCustomEvent({
                     eventCategory: 'Edit Comment',
                     eventAction: 'Edit Comment',
                  });
               };

               commentText.hide();
               commentMeta.hide();
               editContainer.show();

               initializeCommentEditForm(editContainer, commentText, commentMeta, trackCommentEdit);
            },
         });
      },
      'click:relay(.js-comment-reply)': function (ev, el) {
         ev.stop();
         const author = el.get('data-author');
         const post = el.getParent('.js-post');

         post.getElement('.js-add-comment').hide();
         const commentForm = post.getElement('.js-comment-form');
         const textArea = commentForm.getElement('textarea');

         initializeCommentForm(commentForm);
         commentForm.show();

         textArea.focus();
         textArea.set('value', '@' + author + ' ');
      },
      'click:relay(.js-comments-more)': function (ev, el) {
         showHiddenComments(el);
      },
      'click:relay(.js-watch-post)': function (ev, el) {
         trackAnalyticsCustomEvent({
            eventCategory: 'View Answer',
            eventAction: 'View Answer - Btn I have this problem',
         });

         let isWatching = el.get('data-isWatching');
         let shouldWatch = isWatching == 'true' ? 'off' : 'on';
         let parent = el.getParent('.js-post');
         let postid = parent.get('data-postid');

         function createWatchPromise(watchType) {
            let email = App._tempEmail ? App._tempEmail : null;
            return new Request.AjaxIO(watchType, {
               onSuccess: subscribeResponseHandler,
            }).send(shouldWatch, 'post', postid, email);
         }

         function subscribeResponseHandler(response) {
            if (!response || response === 1) {
               let isLoggedIn = Auth.isLoggedIn();

               if (shouldWatch === 'on' && isLoggedIn) {
                  // if the user is logged in,
                  // upvote the question
                  let voteEl = parent.getElement('.js-button-vote.js-vote-yes');
                  postVoteAction(voteEl, 'up', 'none', postid);
               }

               $$('.js-not-watching').setStyle(
                  'display',
                  shouldWatch === 'on' ? 'none' : 'inline-block'
               );
               $$('.js-watching').setStyle(
                  'display',
                  shouldWatch === 'on' ? 'inline-block' : 'none'
               );

               trackAnalyticsCustomEvent({
                  eventCategory: 'Question Subscribe',
                  eventAction: shouldWatch === 'on' ? 'Subscribe' : 'Unsubscribe',
               });
            }
         }

         if (!Auth.isLoggedIn() && !App._tempEmail) {
            Modal.open({
               type: 'module',
               name: 'Watch',
               boxClass: 'watchModal',
            });
            Modal.addEvent('afterLogIn', () => {
               createWatchPromise('watch');
               window.location.reload();
            });
            Modal.addEvent('emailSubmitted', () => {
               subscribeResponseHandler();
            });
         } else if (App._tempEmail) {
            LoadingIndicator.withPromise(createWatchPromise('watchByEmail'));
         } else {
            LoadingIndicator.withPromise(createWatchPromise('watch'), {
               failureMessage: _js('Invalid Account Email'),
            });
         }
      },
      'click:relay(.js-answer-question)': async function (ev, el) {
         trackAnalyticsCustomEvent({
            eventCategory: 'Add Answer',
            eventAction: 'Add Answer - Btn "Answer this question"',
         });

         await getEditor();

         let answerForm = $('answer-form');
         let answerFormPosition = answerForm.getPosition();
         window.scrollTo(0, answerFormPosition.y);

         document.querySelector('#answerEditor').focus();
      },
      'click:relay(.js-view-accepted)': function (ev, el) {
         trackAnalyticsCustomEvent({
            eventCategory: 'View Answer',
            eventAction: 'View Answer - Btn View the answer',
         });

         let acceptedAnswer = $$('.post-accepted')[0];
         let answerPosition = window.scrollTo(0, acceptedAnswer.getPosition().y);
      },
      'click:relay(.js-action-delete)': function (ev, el) {
         const docid = el.get('data-docid');
         const doctype = el.get('data-doctype');

         const modalContent = document.createElement('div');
         modalContent.setAttribute('id', 'deleteCommentModule');

         Modal.open({
            type: 'element',
            element: modalContent,
         });

         const root = createRoot(document.querySelector('#deleteCommentModule'));
         root.render(<ConfirmDelete docid={docid} doctype={doctype} />);
      },
   });

   function updateSortParam(newSortValue) {
      const currentURL = window.location.href;
      const url = new URL(currentURL);
      const params = new URLSearchParams(url.search);
      const SORT_DELETED = 'Show Deleted'; // Matches Question::SORT_DELETED

      // Clear the permalink if we were linking to a deleted answer
      if (params.get('sort') === SORT_DELETED && App.showingDeletedContent) {
         params.delete('permalink');
         url.hash = '';
      }

      if (newSortValue === SORT_DEFAULT) {
         // We don't want to add `?sort=Default` in the URL
         params.delete('sort');
      } else {
         params.set('sort', newSortValue);
      }

      url.search = params.toString();

      history.replaceState({}, '', url.href);
   }

   function getSortParamValue() {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      return params.get('sort');
   }

   async function getEditor() {
      if (!editorid) {
         let pm = document.querySelector('#answerEditor');
         let { getOrCreateHandle } = await import(
            /* webpackPrefetch: true */
            'Shared/prosemirror/answers.jsx'
         );
         editorid = getOrCreateHandle(pm, '#answer-form');
      }
   }

   function updateTextStatus(textarea, charCounter, maxCharacters, minCharacters) {
      let count = textarea.value.trim().length;
      charCounter.textContent = `${textarea.value.length}/${maxCharacters}`;

      charCounter.toggleClass('invalid', count < minCharacters);
   }

   function addTextareaEvents(textarea, charCounter, maxCharacters, minCharacters) {
      textarea.addEventListener('keyup', ev => {
         updateTextStatus(textarea, charCounter, maxCharacters, minCharacters);
         // Autoresize comment textarea
         textarea.rows = Math.ceil(textarea.value.length / 60) + 2;
      });

      textarea.addEventListener('keydown', ev => {
         if (ev.key === ' ' || ev.key === 'Backspace') {
            updateTextStatus(textarea, charCounter, maxCharacters, minCharacters);
         }
      });
   }

   function initializeCharacterCount(textarea, charCounter) {
      const maxCharacters = Number.parseInt(charCounter.dataset.maximum, 10);
      const minCharacters = Number.parseInt(charCounter.dataset.minimum, 10);

      charCounter.textContent = `${textarea.value.length}/${maxCharacters}`;
      charCounter.toggleClass('invalid', textarea.value.length < minCharacters);

      return { maxCharacters, minCharacters };
   }

   function initializeCommentEditForm(editContainer, commentText, commentMeta, trackCommentEdit) {
      const textarea = editContainer.querySelector('textarea');
      const charCounter = editContainer.querySelector('.js-current-characters');
      const { maxCharacters, minCharacters } = initializeCharacterCount(textarea, charCounter);

      addTextareaEvents(textarea, charCounter, maxCharacters, minCharacters);

      commentEditFormManager = editContainer.retrieve('FormManager:formManager');
      if (!commentEditFormManager) {
         commentEditFormManager = new FormManager(editContainer);
      }
      commentEditFormManager.clearAllErrors();

      commentEditFormManager.setValidator(
         textarea,
         getCharacterCountError.bind(null, textarea, minCharacters)
      );

      const submitHandler = editContainer.getElement('.js-submit-comment-edit');
      if (!submitHandler.hasClass('submit-handler')) {
         submitHandler.addClass('submit-handler').addEvent('click', event => {
            if (getCharacterCountError(textarea, minCharacters)) {
               textarea.focus();
               return;
            }

            const commentid = event.target.get('data-commentid');
            const text = textarea.value;

            new Request.AjaxIO('editComment', {
               onSuccess: function (res) {
                  trackCommentEdit();
                  editContainer.hide();
                  commentText.set('html', res.text);
                  commentText.show();
                  commentMeta.show();
               },
            }).send(commentid, text);
         });
      }
   }

   function initializeCommentForm(el) {
      const textarea = el.querySelector('textarea');
      const charCounter = el.querySelector('.js-current-characters');
      const { maxCharacters, minCharacters } = initializeCharacterCount(textarea, charCounter);

      addTextareaEvents(textarea, charCounter, maxCharacters, minCharacters);

      commentFormManager = el.retrieve('FormManager:formManager');
      if (!commentFormManager) {
         commentFormManager = new FormManager(el);
      }
      commentFormManager.clearAllErrors();

      commentFormManager.setValidator(
         textarea,
         getCharacterCountError.bind(null, textarea, minCharacters)
      );

      commentFormManager.addSubmitHandler(() => {
         Auth.required({
            message: _js('Log in to comment on a question.'),
            onAuthorize: function () {
               if (!window.iFixit_clickedSubmit) {
                  window.iFixit_clickedSubmit = true;
                  commentFormManager.submitNow();
               }
            },
         });
      });
   }

   function getCharacterCountError(textarea, minCharacters) {
      const text = textarea.value.trim().replace(/\r\n?/, '\n').replace(/ +/, ' ');
      if (text.length < minCharacters) {
         /* Translators: %1 -> min characters; %2 -> character length; */
         return ___p(
            text.length,
            'Your comment is too short. Please write something over %1 characters. It is currently 1 character.',
            'Your comment is too short. Please write something over %1 characters. It is currently %2 characters.',
            minCharacters,
            text.length
         );
      }

      let parts = text.split('\n');
      if (parts.length >= 1001) {
         /* Translators: %1 -> number of lines; */
         return _js('This comment has %1 lines. You should shorten it.').replace(
            '%1',
            parts.length - 1
         );
      }
      return null;
   }

   function replaceAnswersAndQuestionHtml() {
      const showQuestionComments = Boolean(document.querySelector('#question-details')?.open);
      const getAnswersPromise = new Request.AjaxIO('getAnswers', {
         onSuccess: function (response) {
            if (response.error) {
               Modal.alert(response.error);
               return false;
            }

            let deviceContainer = $E('.question-device-container');
            if (deviceContainer) {
               deviceContainer.dispose();
            }
            $$('.js-question-container').set('html', response.question);
            $$('.js-answers-list').set('html', response.answers);
            $$('.js-filter-option').removeClass('filter-option-active');

            if (deviceContainer) {
               // We don't rerender the device frame module, so we have to re-add it.
               // The template thinks that it has no device, so we have to drop the
               // extra class it adds.
               $E('.js-post-question').removeClass('post-no-device').grab(deviceContainer, 'top');
            }

            allCommentsLoaded = true;
         },
      }).send({ questionid: questionid, sort: currentSort, showQuestionComments });

      LoadingIndicator.withPromise(getAnswersPromise, {
         pendingMessage: _js('Loading answers') + '...',
         hideSuccessMessage: true,
      });
      return getAnswersPromise;
   }

   function postVoteAction(el, direction, curDirection, postid) {
      Auth.required({
         message: _js('Log in to vote.'),
         onAuthorize: function () {
            if (direction == 'up') {
               direction = 1;
            } else if (direction == 'down') {
               direction = -1;
            } else if (direction == 'undo') {
               direction = curDirection == 'up' ? 1 : -1;
            }

            if (curDirection == 'up') {
               curDirection = 1;
            } else if (curDirection == 'down') {
               curDirection = -1;
            }

            new Request.AjaxIO('ajaxVote', {
               onSuccess: async function (response) {
                  if (response.error) {
                     const tooltip = await createTippy(el, {
                        content: response.error,
                        allowHTML: true,
                        interactive: true,
                        appendTo: document.body,
                        onHidden: () => tooltip.destroy(),
                        theme: 'globalTippyStyles',
                     });
                     tooltip.show();
                     return;
                  }

                  let voteContainer = el.getParent('.js-vote-container');
                  let scoreEl = voteContainer.getElements('.js-post-score')[0];

                  scoreEl.set('text', response.score);

                  let yesNoEl = voteContainer.getElements('.js-yes-no-buttons')[0];
                  let votedEl = voteContainer.getElements('.js-buttons-voted')[0];

                  // if the direction is false, the vote was un-done
                  if (response.direction === false || direction == curDirection) {
                     yesNoEl.show();
                     votedEl.hide();
                     voteContainer.set('data-currentVote', 'none');
                  } else {
                     yesNoEl.hide();
                     votedEl.show();

                     voteContainer.set(
                        'data-currentVote',
                        response.direction == -1 ? 'down' : 'up'
                     );

                     voteContainer
                        .getElements('.js-button-voted')[0]
                        .set(
                           'class',
                           'voted-action js-button-voted ' +
                              (response.direction == -1 ? 'voted-no' : 'voted-yes')
                        )
                        .set(
                           'html',
                           response.direction == -1
                              ? '<i class="fa fa-times"></i> ' + _js('Voted No')
                              : '<i class="fa fa-check"></i> ' + _js('Voted Yes')
                        );
                  }
               },
            }).send(postid, direction, curDirection);
         },
      });
   }
});
